//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import { FilterValue } from 'components/inputs/types';
import { BusinessDashboardCard, CardPosition } from 'pages/business-studio/business-dashboard/types';
import {
  DataStudioRecordDetailResponse,
  DataStudioRecordsResponse,
  EntityFilter,
  EntityRecord,
} from 'store/data-studio/types';
import { Entity } from 'store/entity/types';
import { PaginatedApi } from 'store/types';

/**
 * Business Studio specific Entity type that extends the base Entity type
 * with additional fields returned by Business Studio API endpoints.
 * This keeps Business Studio changes isolated to avoid regression issues.
 */
export interface BusinessEntity extends Entity {
  recordCount?: number;
  queuedCount?: number;
}

export const MERGE_REVIEW_QUEUE_ID = '_merge_review';
export const VALIDATION_REVIEW_QUEUE_ID = '_validation_review';

export interface BusinessStudioQueue {
  queueId: string;
  displayName: string;
  pendingCount: number;
  icon?: string;
}

/**
 * Entity schema response structure from /api/v1/studio/schema/entity/{entityId}
 * Returns field definitions with published/draft versions
 */
export interface EntitySchemaFieldData {
  apiName: string;
  draft: any | null;
  published: {
    fields: {
      apiName: string;
      displayName: string;
      dataType: string;
      id: string;
      description?: string;
      isRequired?: boolean;
      isReadonly?: boolean;
      isSystem?: boolean;
      [key: string]: any; // Allow other field properties
    };
    id: string;
  };
}

export interface EntitySchemaResponse {
  meta: Record<string, any>;
  data: EntitySchemaFieldData[];
}

export type BusinessStudioRecordsResponse = DataStudioRecordsResponse;

export interface GetBusinessStudioRecordsParams extends PaginatedApi {
  entityId: string;
  predicate?: FilterValue;
  orderBy?: string;
  sortDirection?: string;
  page?: string;
}

export type QueueRecordDirection = 'NEXT' | 'PREVIOUS';

export interface GetBusinessStudioQueueRecordsParams {
  entityId: string;
  queueId: string;
  count?: number;
  cursor?: string;
  direction?: QueueRecordDirection;
  orderBy?: string;
  sortDirection?: string;
  predicate?: FilterValue;
}

export interface GetRecordParams {
  entityId: string;
  recordId: string;
}

export interface DeleteRecordParams {
  entityId: string;
  recordId: string;
}

export interface CreateBusinessStudioRecordParams {
  entityId: string;
  body: Record<string, any>;
  ignoreWarnings?: boolean;
}

export interface UpdateBusinessStudioRecordParams {
  entityId: string;
  recordId: string;
  body: Record<string, any>;
  ignoreWarnings?: boolean;
}

export interface RecordFieldError {
  code: string;
  message: string;
}

/**
 * Record response - extends DataStudioRecordDetailResponse with Business Studio specific fields
 * including validation warning fields returned when ignoreWarnings=false
 */
export interface RecordResponse extends DataStudioRecordDetailResponse {
  incomingRecord?: EntityRecord;
  existingGoldenRecord?: EntityRecord;
  queueItemId?: string;
  syncariId?: string;
  hasExistingGoldenRecord?: boolean;
  isTest?: boolean;
  warningCount?: number;
  fields?: Record<string, RecordFieldError[]>;
  errors?: {
    record?: RecordFieldError[];
    fields?: Record<string, RecordFieldError[]>;
  };
}

/**
 * Reference to a related entity returned by GET /api/v1/studio/business/entity/{entityId}/references
 * Used when configuring a Table Card in the Add/Edit Card form
 */
export interface EntityReference {
  relatedEntity: string; // ID of the related entity
  relatedEntityDisplayName: string; // Display name of the related entity
  relationField: string; // Field on the related entity that links to this record
  filterValue: string; // Field on this entity to use as the filter value (e.g. "syncariId")
  pageSize: number; // Default page size for the table
}

/** Where a candidate record stands relative to the record the associations hang off */
export enum AssociationStatus {
  /** Already associated — the drawer opens with it checked */
  CURRENTLY_ASSOCIATED = 'CURRENTLY_ASSOCIATED',
  /** Not associated yet, but may be */
  AVAILABLE = 'AVAILABLE',
  /** Excluded by the card's restrict condition — its checkbox is disabled */
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

/** Keys the associations endpoint adds to each record's `values` alongside the real fields */
export const ASSOCIATION_STATUS_KEY = '_associationStatus';
export const ASSOCIATION_SELECTED_KEY = '_selected';

export interface GetRecordAssociationsParams {
  /** Entity of the record the associations hang off */
  entityId: string;
  /** Record the associations hang off */
  recordId: string;
  /** Layout the card lives in — the backend resolves the relationship from the card's config */
  layoutId: string;
  /** Table card the associations are being edited from */
  cardId: string;
  count?: number;
  cursor?: string;
  direction?: 'next' | 'previous';
}

/**
 * One page of records the current record may be associated with.
 *
 * Same shape as the entity records response minus `metadata` — the endpoint returns no field
 * metadata, so column labels come from the card that opened the drawer. Each record carries its
 * status and current selection inside `values`, under the two keys above.
 */
export type RecordAssociationsResponse = Omit<DataStudioRecordsResponse, 'metadata'>;

/** What a single association change does to the record it names */
export enum AssociationChangeAction {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

/** One record to associate or disassociate, as sent to the associations endpoint */
export interface AssociationChange {
  recordId: string;
  action: AssociationChangeAction;
}

export interface UpdateRecordAssociationsParams {
  /** Entity of the record the associations hang off */
  entityId: string;
  /** Record the associations hang off */
  recordId: string;
  /** Layout the card lives in — the backend resolves the relationship from the card's config */
  layoutId: string;
  /** Table card the associations were edited from */
  cardId: string;
  changes: AssociationChange[];
  /**
   * Entity the associated records belong to. Not part of the request — it is only used to
   * invalidate the cached record list so cards showing that entity refresh after a change.
   */
  relatedEntity?: string;
}

export interface EntityLayoutResponse {
  layoutId: string;
  id?: string; // Some API responses return id instead of layoutId
  entityId: string;
  entityName?: string; // Parent entity name (common to all cards)
  name?: string; // Unique name for the layout
  displayName?: string; // Display name of the layout
  description?: string; // Optional description of the layout
  isDefault?: boolean; // Whether this is the default layout
  status?: string; // Layout status (e.g., 'ACTIVE')
  cards: BusinessDashboardCard[];
  cardPositions: CardPosition[]; // Position information stored separately (source of truth)
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEntityLayoutParams {
  entityId: string;
  entityName?: string; // Parent entity name (common to all cards)
  name?: string; // Unique name for the layout (default: 'default')
  displayName?: string; // Display name of the layout
  description?: string; // Optional description of the layout
  isDefault?: boolean; // Whether this is the default layout (required by backend)
  status?: string; // Layout status (required by backend, e.g., 'ACTIVE')
  cards: BusinessDashboardCard[];
  cardPositions: CardPosition[]; // Position information (required)
}

export interface UpdateEntityLayoutParams {
  entityId: string;
  layoutId: string;
  entityName?: string; // Parent entity name (common to all cards)
  name?: string; // Unique name for the layout
  displayName?: string; // Display name of the layout
  description?: string; // Optional description of the layout
  isDefault?: boolean; // Whether this is the default layout (required by backend)
  status?: string; // Layout status (required by backend, e.g., 'ACTIVE')
  cards: BusinessDashboardCard[];
  cardPositions: CardPosition[]; // Position information (required)
}

export interface DeleteEntityLayoutParams {
  entityId: string;
  layoutId: string;
}

/**
 * Business Studio Filter Types
 */
export type BusinessStudioEntityFilter = EntityFilter;

export interface BusinessStudioFiltersResponse {
  pageInfo: {
    count: number;
    filteredCount?: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
  filters: BusinessStudioEntityFilter[];
}

export interface GetBusinessStudioFiltersParams extends PaginatedApi {
  entityId?: string | null;
  bookmarked?: boolean;
  orderBy?: string;
  sortDirection?: string;
}

export interface CreateBusinessStudioFilterParams {
  syncariEntityId: string;
  criteria: FilterValue;
  name: string;
  description: string;
  tags: string[];
  bookmarked: boolean;
}

export interface UpdateBusinessStudioFilterParams {
  filterId: string;
  filter: BusinessStudioEntityFilter;
}

export interface DeleteBusinessStudioFilterParams {
  filterId: string;
}

export interface BookmarkBusinessStudioFilterParams {
  filterId: string;
  bookmarked: boolean;
}

export type QueueRecordAction = string;

export interface GetQueueRecordParams {
  entityId: string;
  queueItemId: string;
}

export interface GetQueueRecordActionsParams {
  entityId: string;
  queueItemId: string;
}

export interface SubmitQueueRecordActionParams {
  entityId: string;
  queueId: string;
  queueItemId: string;
  action?: string;
  fields?: Record<string, any>;
  ignoreWarnings?: boolean;
}

export interface SubmitQueueRecordResponse {
  needsConfirmation?: boolean;
  warningCount?: number;
  error?: string;
  message?: string;
  fields?: Record<string, RecordFieldError[]>;
}

export interface BulkSubmitValidationReviewParams {
  entityId: string;
  queueId: string;
  itemIds: string[];
  action?: string;
  fields?: Record<string, any>;
}

export interface BulkSubmitValidationReviewResult {
  itemId: string;
  syncariId?: string;
  success: boolean;
  submitted: boolean;
  statusCode: number;
  message: string;
  details?: {
    error?: string;
    fields?: Record<string, RecordFieldError[]>;
    record?: RecordFieldError[];
  };
}

export interface BulkSubmitValidationReviewResponse {
  requestedCount: number;
  processedCount: number;
  successCount: number;
  failureCount: number;
  submittedCount: number;
  keptInQueueCount: number;
  results: BulkSubmitValidationReviewResult[];
}

export interface QueueEntityRecord extends EntityRecord {
  queueItemId: string;
  assignedRoleId?: string;
  parkedAt?: string;
  errors?: {
    fields?: Record<string, RecordFieldError[]>;
    record?: RecordFieldError[];
  };
  isTest?: boolean;
}

export interface BusinessStudioQueueRecordsResponse extends Omit<DataStudioRecordsResponse, 'records'> {
  records: QueueEntityRecord[];
}

/**
 * A single conflicting record inside a merge event.
 * Extends EntityRecord so it carries `values`, `syncariId`, etc.
 * `recommendedFields` lists the field keys for which this record holds the
 * system-recommended value.
 */
export interface MergeEventRecord extends EntityRecord {
  recommendedFields: string[];
}

/**
 * Response from GET /arcade/api/v1/studio/business/entity/{entityId}/merge-records/{mergeRecordId}
 *
 * Shape mirrors the standard records API so the same metadata.fields
 * structure can be reused for label/dataType lookups.
 */
export interface MergeEventResponse {
  records: MergeEventRecord[];
  metadata: {
    selectedColumns: string[];
    fields: Record<
      string,
      {
        dataType: string;
        label: string;
        fieldId: string;
        canFilter: boolean;
        canDisplay: boolean;
        canEdit: boolean;
        isSystem?: boolean;
      }
    >;
  };
}

export interface GetMergeEventParams {
  entityId: string;
  mergeRecordId: string;
}

/**
 * Currency option returned by GET arcade/api/v1/currencies
 */
export interface CurrencyOption {
  symbol: string;
  code: string;
  displayName: string;
  isCommon?: boolean | string;
}
